import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization, useUserOrganizationSettings } from '@modules-deprecated/app/organization'
import { useUmbrella } from '@modules-deprecated/app/umbrellas'

import { EmberRoute } from '../../../../../enums/emberRoute'
import { useEmberRouteUrl } from '../../../../../hooks/routing/useEmberRouteUrl'
import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { FastItems } from '../types/fastItems'
import { MenuNavItem } from '../types/menuNavItem'
import { getFastMenuFilteredItems } from '../utils/getFastMenuFilteredItems'

interface FastMenuItems {
  items: MenuNavItem[]
  isLoading: boolean
}

export const useFastMenuItems = (): FastMenuItems => {
  const { t } = useTranslation()
  const { organizationSettings, isLoading: isLoadingUserOrganizationSettings } = useUserOrganizationSettings()
  const { umbrella } = useUmbrella()

  const isInboxEnabled = organizationSettings?.isInboxEnabled === '1'

  const { organization, isLoading: isLoadingOrganization } = useUserOrganization()
  const { url: invoicesNewUrl } = useEmberRouteUrl(EmberRoute.InvoicesNew)
  const { url: quotesNewUrl } = useEmberRouteUrl(EmberRoute.QuotesNew)

  const isLoadingLinks = !invoicesNewUrl || !quotesNewUrl || isLoadingOrganization

  const fastItems = useMemo((): FastItems => {
    if (isLoadingLinks || !organization?.url) {
      return {}
    }

    return {
      newInvoice: {
        id: 'new-invoice',
        children: t('invoice'),
        value: EmberRoute.InvoicesNew,
        iconLeft: 'file',
        to: invoicesNewUrl,
      },
      newBill: {
        id: 'new-bill',
        children: t('bill'),
        value: reactRoute.organization.bills.getNewBillRoute(),
        iconLeft: 'receiptText',
        to: getOrganizationViewUrl(organization.url, reactRoute.organization.bills.getNewBillRoute()),
      },
      newQuote: {
        id: 'new-quote',
        children: t('quote'),
        value: EmberRoute.QuotesNew,
        iconLeft: 'filePenLine',
        to: quotesNewUrl,
      },
    }
  }, [isLoadingLinks, organization?.url, t, invoicesNewUrl, quotesNewUrl])

  // ⛔️ WARNING getFastMenuFilteredItems not memoized on purpose as it uses the global object inside
  const items = getFastMenuFilteredItems({
    fastItems,
    isNewBillItemDisabled: isInboxEnabled,
    isUmbrellaContext: !!umbrella,
  })

  return { items, isLoading: isLoadingUserOrganizationSettings || isLoadingLinks }
}
